import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  HowToVoteOutlined,
  LockOutlined,
  PersonOutlineOutlined,
  PlaceOutlined,
  SearchOutlined,
  ShieldOutlined,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { iebcApi } from '@services/api/iebc';
import { BrandPanel, CoatOfArms, FlagStripe, LoginBrand } from '@pages/USAJILILoginPage';
import { USAJILIThemeProvider, USAJILIColorModeToggle } from '@theme/USAJILIThemeProvider';

const IEBC_BRAND: LoginBrand = {
  title: 'SDICS · IEBC',
  tagline: 'Unregistered voters in your area, with their location down to the village.',
  features: [
    { icon: <HowToVoteOutlined />, title: 'Unregistered voters', text: 'Everyone in your area who is not yet on the voter roll.' },
    { icon: <PlaceOutlined />, title: 'Your location', text: 'Only the location you are assigned to, down to the village.' },
    { icon: <SearchOutlined />, title: 'Search', text: 'Find a person by ID number or name.' },
  ],
  home: '/iebc',
};

type FormValues = { username: string; password: string };

/** Usernames are <phone>@sdics.tech, e.g. 0725926141@sdics.tech. The bare
 *  phone number is accepted too; the server normalises both. */
const looksLikeUsername = (v: string) => {
  const s = v.trim().toLowerCase().replace(/@sdics\.tech$/, '').replace(/[\s+-]/g, '');
  return /^(254|0)?\d{9}$/.test(s);
};

const LoginInner: React.FC = () => {
  const navigate = useNavigate();
  const [showPw, setShowPw] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>();

  const onSubmit = handleSubmit(async ({ username, password }) => {
    setLoading(true);
    setErr(null);
    try {
      await iebcApi.login(username.trim(), password);
      navigate(IEBC_BRAND.home, { replace: true });
    } catch (e: any) {
      const code = e?.error?.code;
      setErr(
        code === 'RATE_LIMIT'
          ? 'Too many wrong attempts. Wait 15 minutes and try again.'
          : code === 'INVALID_CREDENTIALS'
            ? 'Wrong username or password.'
            : e?.error?.message || 'Could not sign in. Check your connection and try again.',
      );
    } finally {
      setLoading(false);
    }
  });

  const iconSx = { color: 'text.secondary', fontSize: 20 };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '1.1fr 1fr' },
        bgcolor: 'background.default',
      }}
    >
      <BrandPanel brand={IEBC_BRAND} />

      <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', px: { xs: 2, sm: 4 }, py: 6 }}>
        <Box sx={{ position: 'absolute', top: 16, right: 16 }}>
          <USAJILIColorModeToggle />
        </Box>

        <Box
          sx={(t) => ({
            width: '100%',
            maxWidth: 420,
            bgcolor: 'background.paper',
            borderRadius: 3,
            overflow: 'hidden',
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: t.palette.mode === 'dark' ? '0 20px 50px rgba(0,0,0,0.45)' : `0 20px 50px ${alpha('#0f1419', 0.12)}`,
          })}
        >
          <FlagStripe />
          <Box sx={{ p: { xs: 3, sm: 4 } }}>
            <Stack alignItems="center" spacing={1} sx={{ display: { xs: 'flex', md: 'none' }, mb: 3 }}>
              <CoatOfArms size={64} />
              <Typography sx={{ fontWeight: 700, fontSize: '1.4rem', letterSpacing: 1, color: 'primary.main' }}>
                {IEBC_BRAND.title}
              </Typography>
            </Stack>

            <Typography sx={{ fontWeight: 700, fontSize: '1.5rem', color: 'text.primary' }}>Sign in</Typography>
            <Typography sx={{ mt: 0.5, mb: 3, fontSize: '0.9rem', color: 'text.secondary' }}>
              Use the username and password you were given.
            </Typography>

            {err && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErr(null)}>
                {err}
              </Alert>
            )}

            <form onSubmit={onSubmit} noValidate>
              <Stack spacing={2.25}>
                <TextField
                  label="Username"
                  placeholder="0725926141@sdics.tech"
                  type="email"
                  inputMode="email"
                  autoComplete="username"
                  autoCapitalize="none"
                  autoFocus
                  fullWidth
                  {...register('username', {
                    required: 'Username is required',
                    validate: (v) => looksLikeUsername(v) || 'Enter your username, e.g. 0725926141@sdics.tech',
                  })}
                  error={!!errors.username}
                  helperText={errors.username?.message as string | undefined}
                  disabled={loading}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonOutlineOutlined sx={iconSx} />
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  label="Password"
                  autoComplete="current-password"
                  type={showPw ? 'text' : 'password'}
                  fullWidth
                  {...register('password', { required: 'Password is required' })}
                  error={!!errors.password}
                  helperText={errors.password?.message as string | undefined}
                  disabled={loading}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockOutlined sx={iconSx} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPw((s) => !s)}
                          edge="end"
                          aria-label={showPw ? 'Hide password' : 'Show password'}
                          sx={{ color: 'text.secondary' }}
                        >
                          {showPw ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <Button
                  fullWidth
                  type="submit"
                  size="large"
                  variant="contained"
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={18} color="inherit" /> : undefined}
                  sx={{ mt: 0.5, py: 1.4, fontWeight: 700, letterSpacing: 0.5 }}
                >
                  {loading ? 'Signing in…' : 'Sign in'}
                </Button>
              </Stack>
            </form>

            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 3, color: 'text.secondary' }}>
              <ShieldOutlined sx={{ fontSize: 16 }} />
              <Typography sx={{ fontSize: '0.78rem' }}>
                Authorised IEBC personnel only. Every search is logged.
              </Typography>
            </Stack>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export const IEBCLoginPage: React.FC = () => (
  <USAJILIThemeProvider>
    <LoginInner />
  </USAJILIThemeProvider>
);

export default IEBCLoginPage;
